import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";

/**
 * External store of in-flight rasterize slots that the Print button waits on.
 *
 * The store object — not the changing count — is the context value, so
 * row components claiming slots never re-render on count changes. A
 * naive useState counter would fire 800 re-renders per rasterize
 * completion on a large report and hang the tab.
 */

export type TimingStats = {
  count: number;
  minMs: number;
  maxMs: number;
  avgMs: number;
  p50Ms: number;
  p95Ms: number;
};

export type DebugSnapshot = {
  pending: number;
  completed: number;
  totalRegistered: number;
  firstRegisteredAt: number | null; // performance.now timestamp
  lastReleasedAt: number | null;
  slotStats: TimingStats | null;
  events: Record<string, number>; // named markers → performance.now
};

type Store = {
  getPendingSnapshot: () => number;
  getDebugSnapshot: () => DebugSnapshot;
  subscribe: (cb: () => void) => () => void;
  register: () => () => void;
  markEvent: (name: string) => void;
};

const Context = createContext<Store | null>(null);

function computeStats(durations: number[]): TimingStats | null {
  if (durations.length === 0) return null;
  const sorted = [...durations].sort((a, b) => a - b);
  const sum = sorted.reduce((a, b) => a + b, 0);
  const pick = (p: number): number =>
    sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))];
  return {
    count: sorted.length,
    minMs: sorted[0],
    maxMs: sorted[sorted.length - 1],
    avgMs: sum / sorted.length,
    p50Ms: pick(0.5),
    p95Ms: pick(0.95)
  };
}

function createStore(): Store {
  let pending = 0;
  let completed = 0;
  let totalRegistered = 0;
  let firstRegisteredAt: number | null = null;
  let lastReleasedAt: number | null = null;
  const durations: number[] = [];
  const events: Record<string, number> = {};
  const listeners = new Set<() => void>();
  const notify = () => listeners.forEach((l) => l());

  return {
    getPendingSnapshot: () => pending,
    getDebugSnapshot: () => ({
      pending,
      completed,
      totalRegistered,
      firstRegisteredAt,
      lastReleasedAt,
      slotStats: computeStats(durations),
      events: { ...events }
    }),
    subscribe: (cb) => {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    register: () => {
      const startedAt = performance.now();
      pending += 1;
      totalRegistered += 1;
      if (firstRegisteredAt === null) firstRegisteredAt = startedAt;
      notify();
      let released = false;
      return () => {
        if (released) return;
        released = true;
        const endedAt = performance.now();
        pending = Math.max(0, pending - 1);
        completed += 1;
        lastReleasedAt = endedAt;
        durations.push(endedAt - startedAt);
        notify();
      };
    },
    markEvent: (name) => {
      events[name] = performance.now();
      notify();
    }
  };
}

export function PrintReadinessProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const storeRef = useRef<Store | null>(null);
  if (!storeRef.current) storeRef.current = createStore();
  return (
    <Context.Provider value={storeRef.current}>{children}</Context.Provider>
  );
}

export function usePrintReadinessPending(): number {
  const store = useContext(Context);
  const [pending, setPending] = useState<number>(() =>
    store ? store.getPendingSnapshot() : 0
  );

  useEffect(() => {
    if (!store) return undefined;
    setPending(store.getPendingSnapshot());
    return store.subscribe(() => setPending(store.getPendingSnapshot()));
  }, [store]);

  return pending;
}

export function usePrintDebugSnapshot(enabled: boolean): DebugSnapshot | null {
  const store = useContext(Context);
  const [snapshot, setSnapshot] = useState<DebugSnapshot | null>(() =>
    store && enabled ? store.getDebugSnapshot() : null
  );

  useEffect(() => {
    if (!store || !enabled) return undefined;
    let raf: number | null = null;
    // rAF debounces a flurry of completions in the same frame to one redraw.
    const tick = () => {
      raf = null;
      setSnapshot(store.getDebugSnapshot());
    };
    const unsubscribe = store.subscribe(() => {
      if (raf === null) raf = requestAnimationFrame(tick);
    });
    setSnapshot(store.getDebugSnapshot());
    return () => {
      unsubscribe();
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, [store, enabled]);

  return enabled ? snapshot : null;
}

export function useMarkPrintEvent(): (name: string) => void {
  const store = useContext(Context);
  return useCallback(
    (name: string) => {
      store?.markEvent(name);
    },
    [store]
  );
}

// Returns an early-release fn; the slot also releases on unmount.
// `active=false` opts out — useful when the component already has its
// result cached.
export function usePrintReadinessSlot(active: boolean): () => void {
  const store = useContext(Context);
  const releaseRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!store || !active) return undefined;
    releaseRef.current = store.register();
    return () => {
      releaseRef.current?.();
      releaseRef.current = null;
    };
  }, [store, active]);

  return useCallback(() => {
    releaseRef.current?.();
    releaseRef.current = null;
  }, []);
}
